import { useMemo } from "react";
import { useHttp } from "../http/useHttp";
import { useAxiosQuery, UseAxiosQueryOptions } from "../http/useAxiosQuery";
import { useDefaultError } from "@synota-io/synota-shared-ui";
import { paths } from "../generated-types";

type PaymentsOperation = paths["/dashboard/contracts/{contract_shared_uuid}/payments"]["post"];
type UsePaymentsResponse = PaymentsOperation["responses"][200]["content"]["application/json"];
type UsePaymentsRequestBody = PaymentsOperation["requestBody"]["content"]["application/json"];
type PaymentsPath = PaymentsOperation["parameters"]["path"];

export type IPayment = UsePaymentsResponse["items"][number];
export type UsePaymentsInput = UsePaymentsRequestBody & {
  contract_uuid: PaymentsPath["contract_shared_uuid"];
};

export const usePayments = (
  input: UsePaymentsInput,
  options: Omit<UseAxiosQueryOptions<UsePaymentsResponse>, "queryKey" | "queryFn"> = {},
) => {
  const { client } = useHttp();

  const { status, isPending, isFetching, isLoading, data, error, refetch } =
    useAxiosQuery<UsePaymentsResponse>({
      queryKey: ["contract-payments", input],
      queryFn: ({ signal }) =>
        client.post(`/contracts/${input.contract_uuid}/payments`, input, { signal }),
      enabled: options.enabled !== false && !!input.contract_uuid,
      ...options,
    });

  const [payments, hasNextPage] = useMemo(() => {
    if (!data) {
      return [[], false];
    }
    return [data.data.items, data.data.has_next_page];
  }, [data]);

  useDefaultError(error);

  return {
    payments,
    hasNextPage,
    error,
    isLoading,
    isFetching,
    isPending,
    status,
    refetch,
  };
};

export type UsePaymentsReturn = ReturnType<typeof usePayments>;
